import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import TaxonomySelector from './TaxonomySelector';
import PathwayScopeToggle from './PathwayScopeToggle';
import { filteredPathwaysForScope } from '../utils/pathwayTaxonomy';
import './CourseBuilderTaxonomyModal.css';

const CourseBuilderTaxonomyModal = ({
  isOpen,
  onClose,
  onTaxonomyChange,
  currentTaxonomy = [],
  primaryConcentration = '',
  otherConcentrations = [],
  concentrations = [],
  currentHourAwards = []
}) => {
  const [localPrimaryConcentration, setLocalPrimaryConcentration] = useState(primaryConcentration);
  const [localTaxonomy, setLocalTaxonomy] = useState([...currentTaxonomy]);
  const [localOtherConcentrations, setLocalOtherConcentrations] = useState([...otherConcentrations]);
  const [selectedConcentrations, setSelectedConcentrations] = useState([]);
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const [primaryScopeFilter, setPrimaryScopeFilter] = useState('concentration');
  const [taxonomyScopeFilter, setTaxonomyScopeFilter] = useState('concentration');

  const pathwayRoots = concentrations;
  const hasCtePathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'concentration'),
    [pathwayRoots]
  );
  const hasSubjectPathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'course_subject'),
    [pathwayRoots]
  );

  const primaryOptionsForScope = useMemo(
    () => filteredPathwaysForScope(pathwayRoots, primaryScopeFilter),
    [pathwayRoots, primaryScopeFilter]
  );

  const taxonomyPathwayOptions = useMemo(
    () =>
      filteredPathwaysForScope(pathwayRoots, taxonomyScopeFilter, {
        alwaysIncludeId: localPrimaryConcentration || null
      }),
    [pathwayRoots, taxonomyScopeFilter, localPrimaryConcentration]
  );

  // Initialize selectedConcentrations when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 CourseBuilderTaxonomyModal initializing with:', {
        primaryConcentration,
        otherConcentrations,
        currentTaxonomyLength: currentTaxonomy?.length || 0,
        currentTaxonomy
      });
      
      const initialSelectedConcentrations = [];
      if (primaryConcentration) {
        initialSelectedConcentrations.push(primaryConcentration);
      }
      if (otherConcentrations && otherConcentrations.length > 0) {
        initialSelectedConcentrations.push(...otherConcentrations);
      }
      setSelectedConcentrations(initialSelectedConcentrations);
      
      // Reset local state to current values - force reset to avoid stale state
      setLocalPrimaryConcentration(primaryConcentration || '');
      setLocalTaxonomy(Array.isArray(currentTaxonomy) ? [...currentTaxonomy] : []);
      setLocalOtherConcentrations(Array.isArray(otherConcentrations) ? [...otherConcentrations] : []);
      
      // Fetch taxonomy items
      const fetchTaxonomyItems = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get('/api/taxonomy/all', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTaxonomyItems(response.data.items || []);
        } catch (error) {
          console.error('Error fetching taxonomy items:', error);
        }
      };
      
      fetchTaxonomyItems();
      
      console.log('🔍 CourseBuilderTaxonomyModal initialized with:', {
        localPrimaryConcentration: primaryConcentration || '',
        localOtherConcentrations: Array.isArray(otherConcentrations) ? [...otherConcentrations] : [],
        localTaxonomyLength: Array.isArray(currentTaxonomy) ? currentTaxonomy.length : 0,
        selectedConcentrations: initialSelectedConcentrations
      });
    } else {
      // Reset state when modal closes to prevent stale data
      console.log('🔍 CourseBuilderTaxonomyModal closing - resetting state');
      setLocalPrimaryConcentration('');
      setLocalTaxonomy([]);
      setLocalOtherConcentrations([]);
      setSelectedConcentrations([]);
    }
  }, [isOpen, primaryConcentration, otherConcentrations, currentTaxonomy]);

  // Sync selectedConcentrations with localOtherConcentrations changes
  useEffect(() => {
    if (localOtherConcentrations && localOtherConcentrations.length > 0) {
      // Add otherconcentrations to selectedConcentrations if they're not already there
      setSelectedConcentrations(prev => {
        const currentSelected = new Set(prev);
        localOtherConcentrations.forEach(concId => {
          currentSelected.add(concId);
        });
        return Array.from(currentSelected);
      });
    }
  }, [localOtherConcentrations]);

  useEffect(() => {
    if (!isOpen || !localPrimaryConcentration || pathwayRoots.length === 0) return;
    const row = pathwayRoots.find((p) => p.id === localPrimaryConcentration);
    if (row) {
      setPrimaryScopeFilter(row.pathwayKind);
      setTaxonomyScopeFilter(row.pathwayKind);
    }
  }, [isOpen, localPrimaryConcentration, pathwayRoots]);

  useEffect(() => {
    if (!isOpen || pathwayRoots.length === 0) return;
    if (localPrimaryConcentration) return;
    if (!hasCtePathways && hasSubjectPathways) {
      setPrimaryScopeFilter('course_subject');
      setTaxonomyScopeFilter('course_subject');
    }
  }, [isOpen, pathwayRoots, localPrimaryConcentration, hasCtePathways, hasSubjectPathways]);

  const handlePrimaryConcentrationChange = (newPrimaryConcentration) => {
    const oldPrimaryConcentration = localPrimaryConcentration;
    setLocalPrimaryConcentration(newPrimaryConcentration);
    
    // Update selectedConcentrations to handle the primary concentration change
    let updatedSelectedConcentrations = [...selectedConcentrations];
    
    // Remove the old primary concentration from selectedConcentrations
    if (oldPrimaryConcentration) {
      updatedSelectedConcentrations = updatedSelectedConcentrations.filter(
        id => id !== oldPrimaryConcentration
      );
    }
    
    // Add the new primary concentration
    if (newPrimaryConcentration) {
      updatedSelectedConcentrations.push(newPrimaryConcentration);
    }
    
    // If the old primary concentration is no longer primary, add it back to other concentrations
    if (oldPrimaryConcentration && oldPrimaryConcentration !== newPrimaryConcentration) {
      updatedSelectedConcentrations.push(oldPrimaryConcentration);
    }
    
    setSelectedConcentrations(updatedSelectedConcentrations);
    
    // Update localOtherConcentrations to reflect the change
    const newOtherConcentrations = updatedSelectedConcentrations.filter(
      concentrationId => concentrationId !== newPrimaryConcentration
    );
    setLocalOtherConcentrations(newOtherConcentrations);
  };

  const handlePrimaryScopeChange = (scope) => {
    setPrimaryScopeFilter(scope);
    const stillValid = pathwayRoots.some(
      (p) => p.id === localPrimaryConcentration && p.pathwayKind === scope
    );
    if (!stillValid) {
      handlePrimaryConcentrationChange('');
    }
  };

  const handleSelectedConcentrationsChange = (newSelectedConcentrations) => {
    setSelectedConcentrations(newSelectedConcentrations);
    
    // Extract other concentrations from selected concentrations (excluding primary concentration)
    const otherConcentrationsFromSelected = newSelectedConcentrations.filter(
      concentrationId => concentrationId !== localPrimaryConcentration
    );
    
    // Update local other concentrations
    setLocalOtherConcentrations(otherConcentrationsFromSelected);
  };

  const handleTaxonomyChange = (newTaxonomy) => {
    setLocalTaxonomy(newTaxonomy);
  };

  const handleClose = () => {
    console.log('🔍 CourseBuilderTaxonomyModal closing with data:', {
      localTaxonomyLength: localTaxonomy?.length || 0,
      localPrimaryConcentration,
      localOtherConcentrationsLength: localOtherConcentrations?.length || 0
    });
    
    // Call the taxonomy change handler with all three pieces of data (auto-save on close)
    onTaxonomyChange(
      localTaxonomy, 
      localPrimaryConcentration, 
      localOtherConcentrations
    );
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fullscreen-overlay">
      <div className="fullscreen-editor" onClick={(e) => e.stopPropagation()}>
        <div className="fullscreen-header">
          <h3>Pathway & taxonomy for this item</h3>
          <button 
            className="fullscreen-close" 
            onClick={handleClose}
            style={{
              background: 'transparent',
              color: '#374151',
              border: 'none'
            }}
          >×</button>
        </div>
        
        <div className="fullscreen-content">
          {/* Primary pathway (CTE concentration or Course-Subject) */}
          <div className="form-section">
            <h2>Primary pathway</h2>
            <div className="form-group">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <label style={{ margin: 0 }}>
                  Primary pathway <span style={{ color: 'red' }}>*</span>
                </label>
                <PathwayScopeToggle
                  value={primaryScopeFilter}
                  onChange={handlePrimaryScopeChange}
                  hasConcentrations={hasCtePathways}
                  hasSubjects={hasSubjectPathways}
                  idPrefix="cbtm-primary-scope"
                />
              </div>
              <select 
                className="form-control"
                value={localPrimaryConcentration || ''} 
                onChange={(e) => handlePrimaryConcentrationChange(e.target.value)}
                required
              >
                <option value="">
                  {primaryScopeFilter === 'concentration'
                    ? 'Select a CTE pathway'
                    : 'Select a course subject'}
                </option>
                {primaryOptionsForScope.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Taxonomy Selector Section */}
          <div className="form-section">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
                marginBottom: 8,
              }}
            >
              <h2 style={{ margin: 0 }}>Pathways, skills, standards, objectives</h2>
              <PathwayScopeToggle
                value={taxonomyScopeFilter}
                onChange={setTaxonomyScopeFilter}
                hasConcentrations={hasCtePathways}
                hasSubjects={hasSubjectPathways}
                idPrefix="cbtm-taxonomy-scope"
              />
            </div>
            <div style={{ color: '#1D4E89', fontSize: '0.98rem', marginBottom: 12, fontWeight: 500 }}>
              Select additional pathways if needed, then attach skills, standards, and objectives under each pathway or subject.
            </div>
            <TaxonomySelector
              key={`taxonomy-selector-${isOpen ? 'open' : 'closed'}-${localTaxonomy?.length || 0}-${localPrimaryConcentration || 'none'}-${taxonomyScopeFilter}`}
              selectedTaxonomy={localTaxonomy}
              onTaxonomyChange={handleTaxonomyChange}
              concentrations={taxonomyPathwayOptions}
              disabled={false}
              primaryConcentration={localPrimaryConcentration}
              onSelectedConcentrationsChange={handleSelectedConcentrationsChange}
              selectedConcentrations={selectedConcentrations}
              isStudentRole={false}
              taxonomyItems={taxonomyItems}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default CourseBuilderTaxonomyModal;
